import { useMemo, useState } from 'react';
import {
  Anchor,
  CheckCircle2,
  Lock,
  Navigation,
  Play,
  PlaneTakeoff,
  RotateCcw,
  Settings2,
  Unlock,
  WifiOff,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useFleetStore } from '@/stores/fleet';
import { useDashboardStore, type KpiKey } from '@/stores/dashboard';
import {
  computeMachineStatus,
  hasNoCommunication,
  isBlocked,
  isGpsFresh,
  type MachineStatus,
} from '@/lib/machineStatus';
import { cn, formatDateTime } from '@/lib/utils';

interface KpiDef {
  key: KpiKey;
  label: string;
  icon: typeof Play;
  iconBg: string;
  iconColor: string;
  sub?: string;
}

const KPI_DEFS: KpiDef[] = [
  { key: 'running', label: 'Navegando', icon: Play, iconBg: 'bg-status-running', iconColor: 'text-white' },
  { key: 'ready', label: 'Atracada', icon: CheckCircle2, iconBg: 'bg-status-running', iconColor: 'text-white' },
  { key: 'off', label: 'Offline', icon: X, iconBg: 'bg-status-off', iconColor: 'text-white' },
  { key: 'blocked', label: 'Bloqueadas', icon: Lock, iconBg: 'bg-status-blocked/10', iconColor: 'text-status-blocked' },
  { key: 'unblocked', label: 'Desbloqueadas', icon: Unlock, iconBg: 'bg-status-running/10', iconColor: 'text-status-running' },
  { key: 'gpsFresh', label: 'GPS Atualizado', icon: Navigation, iconBg: 'bg-primary/10', iconColor: 'text-primary', sub: '≤ 1h' },
  { key: 'gpsStale', label: 'GPS Desatualizado', icon: PlaneTakeoff, iconBg: 'bg-status-ready/10', iconColor: 'text-status-ready', sub: '> 1h ou sem fix' },
  { key: 'noComm', label: 'Sem comunicação', icon: WifiOff, iconBg: 'bg-status-blocked/10', iconColor: 'text-status-blocked', sub: '+ 10 dias' },
];

export function DashboardPage() {
  const devices = useFleetStore((s) => s.devices);
  const positions = useFleetStore((s) => s.positions);
  const visible = useDashboardStore((s) => s.visible);
  const [configOpen, setConfigOpen] = useState(false);

  const stats = useMemo(() => {
    const status: Record<MachineStatus, number> = { running: 0, ready: 0, blocked: 0, off: 0 };
    let blockedCount = 0;
    let unblockedCount = 0;
    let gpsFresh = 0;
    let gpsStale = 0;
    let noComm = 0;
    let lastFix = 0;
    for (const device of devices.values()) {
      const position = positions.get(device.id);
      status[computeMachineStatus(device, position)] += 1;
      if (isBlocked(device, position)) blockedCount += 1;
      else unblockedCount += 1;
      if (isGpsFresh(device, position)) gpsFresh += 1;
      else gpsStale += 1;
      if (hasNoCommunication(device, position)) noComm += 1;
      const fix = position?.fixTime ? new Date(position.fixTime).getTime() : 0;
      if (fix > lastFix) lastFix = fix;
    }
    return { status, blockedCount, unblockedCount, gpsFresh, gpsStale, noComm, lastFix };
  }, [devices, positions]);

  const total = devices.size;
  const availability =
    total === 0 ? 0 : Math.round(((stats.status.running + stats.status.ready) / total) * 100);

  const valueFor = (key: KpiKey): number => {
    switch (key) {
      case 'running': return stats.status.running;
      case 'ready': return stats.status.ready;
      case 'off': return stats.status.off;
      case 'blocked': return stats.blockedCount;
      case 'unblocked': return stats.unblockedCount;
      case 'gpsFresh': return stats.gpsFresh;
      case 'gpsStale': return stats.gpsStale;
      case 'noComm': return stats.noComm;
      case 'availability': return availability;
    }
  };

  const visibleKpis = KPI_DEFS.filter((k) => visible[k.key]);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Card className="border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent shadow-sm">
        <CardContent className="flex items-center gap-4 p-5">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-brand text-brand-foreground shadow-sm">
            <Anchor className="h-9 w-9" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total de embarcações</p>
            <p className="text-4xl font-bold leading-tight">{total}</p>
            <p className="text-xs text-muted-foreground">
              {stats.lastFix
                ? `Atualizado às ${formatDateTime(new Date(stats.lastFix)).split(', ')[1] ?? formatDateTime(new Date(stats.lastFix))}`
                : 'Aguardando atualização'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setConfigOpen(true)} className="gap-1.5">
            <Settings2 className="h-3.5 w-3.5" />
            Configurar
          </Button>
        </CardContent>
      </Card>

      {visibleKpis.length > 0 && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {visibleKpis.map((def) => (
            <KpiTile key={def.key} def={def} value={valueFor(def.key)} />
          ))}
        </div>
      )}

      {visible.availability && (
        <Card>
          <CardContent className="space-y-3 p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Parque de embarcações</h3>
              <span className="text-sm font-bold text-status-running">{availability}%</span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-status-ready transition-all" style={{ width: `${availability}%` }} />
            </div>
            <p className="text-xs text-muted-foreground">
              Disponibilidade calculada a partir das embarcações navegando ou atracadas.
            </p>
          </CardContent>
        </Card>
      )}

      {visibleKpis.length === 0 && !visible.availability && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-10 text-center">
            <p className="text-sm text-muted-foreground">
              Todos os KPIs estão ocultos. Clique em <strong>Configurar</strong> pra escolher.
            </p>
          </CardContent>
        </Card>
      )}

      {configOpen && <DashboardConfigModal onClose={() => setConfigOpen(false)} />}
    </div>
  );
}

function KpiTile({ def, value }: { def: KpiDef; value: number }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-2 p-4 text-center">
        <span className={cn('flex h-11 w-11 items-center justify-center rounded-full', def.iconBg, def.iconColor)}>
          <def.icon className="h-5 w-5" />
        </span>
        <p className="text-2xl font-bold leading-none">{value}</p>
        <p className="text-xs font-medium text-muted-foreground">{def.label}</p>
        {def.sub && <p className="text-[10px] text-muted-foreground">{def.sub}</p>}
      </CardContent>
    </Card>
  );
}

function DashboardConfigModal({ onClose }: { onClose: () => void }) {
  const visible = useDashboardStore((s) => s.visible);
  const toggle = useDashboardStore((s) => s.toggle);
  const setAll = useDashboardStore((s) => s.setAll);
  const reset = useDashboardStore((s) => s.reset);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-card p-5 shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold">Configurar Dashboard</h3>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Escolha quais KPIs aparecem. As preferências ficam salvas neste navegador.
        </p>
        <ul className="mb-4 space-y-1 rounded-md border border-border bg-card p-2">
          {KPI_DEFS.map((def) => (
            <li key={def.key}>
              <label className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-accent">
                <input
                  type="checkbox"
                  checked={visible[def.key]}
                  onChange={() => toggle(def.key)}
                  className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
                />
                <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full', def.iconBg, def.iconColor)}>
                  <def.icon className="h-3.5 w-3.5" />
                </span>
                <span className="flex-1 text-sm font-medium">{def.label}</span>
              </label>
            </li>
          ))}
          <li>
            <label className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-accent">
              <input
                type="checkbox"
                checked={visible.availability}
                onChange={() => toggle('availability')}
                className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
              />
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-status-ready/15 text-status-ready">
                <Play className="h-3.5 w-3.5" />
              </span>
              <span className="flex-1 text-sm font-medium">Parque de embarcações (barra)</span>
            </label>
          </li>
        </ul>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            <button type="button" onClick={() => setAll(true)} className="text-xs font-semibold text-primary hover:underline">
              Marcar todos
            </button>
            <span className="text-xs text-muted-foreground">·</span>
            <button type="button" onClick={() => setAll(false)} className="text-xs font-semibold text-muted-foreground hover:underline">
              Desmarcar todos
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={reset} className="gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" />
            Padrão
          </Button>
        </div>
      </div>
    </div>
  );
}
